import { useEffect, useState } from "react";
import { Loader2, ShoppingCart, Plus, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";
import { Drawer } from "@/components/layout/Drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Field, DrawerSection } from "@/components/forms/fields";
import { useDuplicateCheck } from "@/hooks/useDuplicateCheck";
import { ordersService } from "@/services/resourceServices";
import { formatApiErrorDetail } from "@/services/api";
import { formatRelativeTime } from "@/lib/format";
import { MARKETPLACES } from "@/lib/constants";

const EMPTY = {
  store_id: "",
  marketplace: "",
  order_id: "",
  customer_name: "",
  supplier: "",
  supplier_order_id: "",
  tracking_number: "",
  shipping_label_url: "",
  notes: "",
};
const EMPTY_PRODUCT = { sku: "", product_name: "", quantity: 1 };

export function OrderDrawer({ open, onOpenChange, record, stores = [], onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [products, setProducts] = useState([{ ...EMPTY_PRODUCT }]);
  const [saving, setSaving] = useState(false);
  const isEdit = !!record;

  useEffect(() => {
    if (open) {
      if (record) {
        setForm({
          store_id: record.store_id || "",
          marketplace: record.marketplace || "",
          order_id: record.order_id || "",
          customer_name: record.customer_name || "",
          supplier: record.supplier || "",
          supplier_order_id: record.supplier_order_id || "",
          tracking_number: record.tracking_number || "",
          shipping_label_url: record.shipping_label_url || "",
          notes: record.notes || "",
        });
        setProducts(record.products?.length ? record.products.map((p) => ({ ...p })) : [{ ...EMPTY_PRODUCT }]);
      } else {
        setForm(EMPTY);
        setProducts([{ ...EMPTY_PRODUCT }]);
      }
    }
  }, [open, record]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const onStoreChange = (id) => {
    const store = stores.find((s) => s.id === id);
    setForm((f) => ({ ...f, store_id: id, marketplace: f.marketplace || store?.marketplace || "" }));
  };

  const setProduct = (i, key, value) =>
    setProducts((ps) => ps.map((p, idx) => (idx === i ? { ...p, [key]: value } : p)));
  const addProduct = () => setProducts((ps) => [...ps, { ...EMPTY_PRODUCT }]);
  const removeProduct = (i) => setProducts((ps) => (ps.length > 1 ? ps.filter((_, idx) => idx !== i) : ps));

  const { result: dup } = useDuplicateCheck(
    () => ordersService.checkDuplicate({ store_id: form.store_id, order_id: form.order_id, exclude_id: record?.id || "" }),
    [form.store_id, form.order_id, open],
    open && !!form.store_id && !!form.order_id.trim()
  );
  const orderWarning = dup?.order_warning;

  const validProducts = products.filter((p) => p.sku.trim() && Number(p.quantity) > 0);
  const storeError = !form.store_id ? "Store is required" : "";
  const orderIdError = !form.order_id.trim() ? "Order ID is required" : "";
  const productsError = validProducts.length === 0 ? "Add at least one product with a SKU and quantity" : "";
  const canSave = !storeError && !orderIdError && !productsError && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const body = {
        ...form,
        products: validProducts.map((p) => ({
          sku: p.sku.trim(),
          product_name: (p.product_name || "").trim(),
          quantity: Number(p.quantity),
        })),
      };
      const res = isEdit ? await ordersService.update(record.id, body) : await ordersService.create(body);
      toast.success(isEdit ? "Order updated" : "Order created");
      onOpenChange(false);
      onSaved?.(res);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      icon={ShoppingCart}
      title={isEdit ? `Edit Order ${record?.order_id || ""}` : "New Order"}
      description={isEdit ? "Update order details and products." : "Create a marketplace order."}
      widthClass="sm:max-w-xl"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="order-drawer-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={!canSave} data-testid="order-drawer-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Save changes" : "Create order"}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <DrawerSection title="Order details">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Store" required error={storeError}>
              <Select value={form.store_id} onValueChange={onStoreChange}>
                <SelectTrigger data-testid="order-store-select"><SelectValue placeholder="Select a store" /></SelectTrigger>
                <SelectContent>
                  {stores.map((s) => (<SelectItem key={s.id} value={s.id}>{s.store_name}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Marketplace">
              <Select value={form.marketplace} onValueChange={set("marketplace")}>
                <SelectTrigger data-testid="order-marketplace-select"><SelectValue placeholder="Marketplace" /></SelectTrigger>
                <SelectContent>
                  {MARKETPLACES.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Order ID" required error={orderIdError} warning={!orderIdError ? (orderWarning ? "This Order ID already exists for this store." : "") : ""}>
            <Input value={form.order_id} onChange={(e) => set("order_id")(e.target.value)} placeholder="ORD-10428" data-testid="order-id-input" />
          </Field>
          <Field label="Customer Name">
            <Input value={form.customer_name} onChange={(e) => set("customer_name")(e.target.value)} placeholder="Jane Doe" data-testid="order-customer-input" />
          </Field>
        </DrawerSection>

        <DrawerSection title="Products">
          <div className="space-y-3">
            {products.map((p, i) => (
              <div key={i} className="grid grid-cols-12 items-end gap-2" data-testid={`order-product-${i}`}>
                <div className="col-span-4">
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">SKU</label>
                  <Input value={p.sku} onChange={(e) => setProduct(i, "sku", e.target.value)} placeholder="FF-100" data-testid={`order-product-sku-${i}`} />
                </div>
                <div className="col-span-5">
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">Product Name</label>
                  <Input value={p.product_name} onChange={(e) => setProduct(i, "product_name", e.target.value)} placeholder="Widget" data-testid={`order-product-name-${i}`} />
                </div>
                <div className="col-span-2">
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">Qty</label>
                  <Input type="number" min={1} value={p.quantity} onChange={(e) => setProduct(i, "quantity", e.target.value)} data-testid={`order-product-qty-${i}`} />
                </div>
                <div className="col-span-1">
                  <button type="button" onClick={() => removeProduct(i)} disabled={products.length === 1}
                    className="flex h-10 w-full items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-error-light hover:text-error disabled:opacity-40"
                    data-testid={`order-product-remove-${i}`} aria-label="Remove product">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          {productsError && <p className="text-xs font-medium text-error-dark">{productsError}</p>}
          <Button variant="outline" size="sm" className="mt-1 gap-2" onClick={addProduct} data-testid="order-add-product">
            <Plus className="h-4 w-4" /> Add product
          </Button>
        </DrawerSection>

        <DrawerSection title="Fulfillment">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Supplier"><Input value={form.supplier} onChange={(e) => set("supplier")(e.target.value)} data-testid="order-supplier-input" /></Field>
            <Field label="Supplier Order ID"><Input value={form.supplier_order_id} onChange={(e) => set("supplier_order_id")(e.target.value)} data-testid="order-supplier-order-input" /></Field>
            <Field label="Tracking Number"><Input value={form.tracking_number} onChange={(e) => set("tracking_number")(e.target.value)} data-testid="order-tracking-input" /></Field>
            <Field label="Shipping Label URL"><Input value={form.shipping_label_url} onChange={(e) => set("shipping_label_url")(e.target.value)} placeholder="https://" data-testid="order-label-input" /></Field>
          </div>
          <Field label="Notes"><Textarea value={form.notes} onChange={(e) => set("notes")(e.target.value)} rows={2} data-testid="order-notes-input" /></Field>
        </DrawerSection>

        {isEdit && record?.timeline?.length > 0 && (
          <DrawerSection title="Timeline">
            <div className="space-y-3">
              {record.timeline.map((n, i) => (
                <div key={i} className="flex gap-3" data-testid={`order-timeline-${i}`}>
                  <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1 rounded-lg border border-border bg-muted/30 px-3 py-2">
                    <p className="text-sm text-foreground">{n.message}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{n.user} · {formatRelativeTime(n.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          </DrawerSection>
        )}
      </div>
    </Drawer>
  );
}
